import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getSitemapData } from './../SitemapComponent';
import SitemapLinksSkeleton from './SitemapLinksSkeleton';
import ErrorState from './../../ErrorState/ErrorState';
import LocalizedNavLink from './../../../hooks/LocalizedNavLink';
import { AnimatedOnScroll } from '../../AnimatedOnScroll';
import './SitemapLinks.scss';

const getLocalizedValue = (value, currentLang) => {
    return typeof value === 'object'
        ? value[currentLang] || value.en
        : value;
};

const SitemapLinks = () => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;
   
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [contentSitemap, setContentSitemap] = useState(null);

    const loadData = useCallback(async () => {
        try {
            setIsLoading(true);
            setHasError(false);
            const data = await getSitemapData(currentLang);
            setContentSitemap(data);
        } catch (error) {
            console.error('Error loading sitemap data:', error);
            setHasError(true);
        } finally {
            setIsLoading(false);
        }
    }, [currentLang]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleRetry = useCallback(() => {
        loadData();
    }, [loadData]);

    const blocks = useMemo(() => {
        if (!contentSitemap?.urls) return [];

        const blockMap = {
            1.0: {
                title: getLocalizedValue(contentSitemap.sitemapHomeTitle, currentLang),
                urls: []
            },
            0.9: {
                title: getLocalizedValue(contentSitemap.sitemapMarketingTitle, currentLang),
                urls: []
            },
            0.8: {
                title: getLocalizedValue(contentSitemap.sitemapServicesTitle, currentLang),
                urls: []
            },
            0.7: {
                title: getLocalizedValue(contentSitemap.sitemapWorkTitle, currentLang),
                urls: []
            },
            0.6: {
                title: getLocalizedValue(contentSitemap.sitemapInsightsTitle, currentLang),
                urls: []
            },
        };

        contentSitemap.urls.forEach((item) => {
            if (blockMap[item.priority]) {
                const localizedItem = {
                    ...item,
                    title: getLocalizedValue(item.title, currentLang)
                };
                blockMap[item.priority].urls.push(localizedItem);
            }
        });

        return Object.values(blockMap);
    }, [contentSitemap, currentLang]);

    const hasValidData = useMemo(() => 
        !hasError && contentSitemap && blocks.length > 0 && blocks.some(block => block.urls.length > 0),
        [hasError, contentSitemap, blocks]
    );

    if (isLoading) {
        return <SitemapLinksSkeleton />;
    }

    if (!hasValidData) {
        return <ErrorState onRetry={handleRetry} />;
    }

    return (
        <div className="sitemap__links__wrapper">
            {blocks.map((block, blockIndex) => (
                <div key={`${block.title}-${blockIndex}`} className="sitemap__block">
                    <AnimatedOnScroll animation="fade-left" delay={0}>
                    <h3 className="sitemap__block__title">{block.title}</h3>
                    <ul className="sitemap__block__list">
                        {block.urls.map((url, urlIndex) => (
                            <li key={`${url.loc}-${urlIndex}-${blockIndex}`} className="sitemap__block__list__item">
                                <LocalizedNavLink className='sitemap__block__list__item__link' to={url.loc}>
                                    {url.title || url.loc}
                                </LocalizedNavLink>
                            </li>
                        ))}
                    </ul>
                    </AnimatedOnScroll>
                </div>
            ))}
        </div>
    );
};

export default React.memo(SitemapLinks);